import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, ChevronDown, LogOut, User, Calendar, LayoutDashboard, ExternalLink, Crown, Shield, Settings, CreditCard, Building2, Sparkles } from 'lucide-react';
import { hasUnseenLaunch, markLatestSeen } from '@/data/featureLaunches';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [unseenLaunch, setUnseenLaunch] = useState(() => hasUnseenLaunch());

  const isDoctor = user?.role === 'doctor';
  const isPatient = user?.role === 'patient';
  const isAdmin = user?.role === 'admin' || user?.role === 'country_admin';
  const isDashboard = location.pathname.startsWith('/dashboard');
  const isMyClinics = location.pathname.startsWith('/my-clinics');
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200/50" data-testid="main-header">
      <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 flex items-center justify-between h-20 md:h-24 lg:h-28 xl:h-32">
        <Link to={isAdmin ? '/admin' : isDoctor ? '/dashboard' : '/'} className="flex items-center gap-3 lg:gap-4 group" data-testid="logo-link">
          <div className="overflow-hidden flex-shrink-0" style={{ height: 'clamp(2.8rem, 3.6vw, 4.4rem)', width: 'clamp(2.8rem, 3.6vw, 4.4rem)' }}>
            <img src="/docsite-logo.png" alt="DocSite" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <span className="font-bold text-slate-900 block" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.125rem, 1.4vw, 1.75rem)' }}>DocSite</span>
            <span className="block text-slate-500 tracking-wider uppercase font-medium" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 'clamp(0.7rem, 0.75vw, 0.875rem)' }}>
              {isAdmin ? 'Admin' : 'for Doctors'}
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10 xl:gap-12" data-testid="desktop-nav">
          {isAdmin && (
            <Link to="/admin" className={`font-medium transition-colors ${isAdminPage ? 'text-sky-600' : 'text-slate-600 hover:text-sky-600'}`} style={{ fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }}>Admin Console</Link>
          )}
          {isDoctor && (
            <>
              <Link to="/dashboard" className={`font-medium transition-colors ${isDashboard ? 'text-sky-600' : 'text-slate-600 hover:text-sky-600'}`} style={{ fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }}>Dashboard</Link>
              {user.slug && (
                <Link to={`/dr/${user.slug}`} className="font-medium text-slate-600 hover:text-sky-600 transition-colors" style={{ fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }}>My Website</Link>
              )}
            </>
          )}
          {isPatient && (
            <Link to="/my-clinics" className={`font-medium transition-colors ${isMyClinics ? 'text-sky-600' : 'text-slate-600 hover:text-sky-600'}`} style={{ fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }} data-testid="nav-my-clinics">My Trusted Medical Clinics</Link>
          )}
          {!isDoctor && !isAdmin && !user && (
            <>
              <a href="/#features" className="font-medium text-slate-600 hover:text-sky-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }}>Features</a>
              <a href="/#how-it-works" className="font-medium text-slate-600 hover:text-sky-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }}>How It Works</a>
              <Link to="/for-patients" className="font-medium text-slate-600 hover:text-sky-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }} data-testid="nav-for-patients">For Patients</Link>
              <Link to="/insights" className="font-medium text-slate-600 hover:text-sky-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }} data-testid="nav-insights">Medical Insights</Link>
              <Link to="/for" className="font-medium text-slate-600 hover:text-sky-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }} data-testid="nav-specialties">Specialties</Link>
              <Link to="/whats-new" onClick={() => { markLatestSeen(); setUnseenLaunch(false); }}
                className="relative inline-flex items-center gap-1 font-medium text-slate-600 hover:text-sky-600 transition-colors"
                style={{ fontFamily: 'Manrope, sans-serif', fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }}
                data-testid="nav-whats-new">
                <Sparkles size={14} className="text-sky-500" />
                What's New
                {unseenLaunch && (
                  <span className="absolute -top-1 -right-2 flex h-2 w-2" aria-label="New launch">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                )}
              </Link>
            </>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors text-sm font-medium"
                data-testid="user-menu-button"
              >
                {isAdmin && <Settings size={14} className="text-slate-700" />}
                {isDoctor && (
                  user.tier === 'turbo'
                    ? <Sparkles size={14} className="text-fuchsia-500" />
                    : user.tier === 'premium'
                    ? <Crown size={14} className="text-amber-500" />
                    : <Shield size={14} className="text-slate-400" />
                )}
                {!isDoctor && !isAdmin && <User size={16} />}
                {user.name}
                <ChevronDown size={14} />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50">
                  {isAdmin && (
                    <Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setUserMenuOpen(false)} data-testid="admin-console-link">
                      <Settings size={14} /> Admin Console
                    </Link>
                  )}
                  {isDoctor && (
                    <>
                      <div className="px-4 py-2 border-b border-slate-100">
                        {user.tier === 'turbo' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-[10px] font-bold shadow-sm">
                            <Sparkles size={10} /> TURBOCHARGE
                          </span>
                        ) : user.tier === 'premium' ? (
                          <div className="flex items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 text-[10px] font-bold border border-amber-200">
                              <Crown size={10} /> PREMIUM
                            </span>
                            <Link to="/upgrade" onClick={() => setUserMenuOpen(false)}
                              className="text-[10px] font-bold text-fuchsia-600 hover:text-fuchsia-700" data-testid="upgrade-link-menu">
                              ↑ TURBO
                            </Link>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold border border-slate-200">
                              <Shield size={10} /> STANDARD
                            </span>
                            <Link to="/upgrade" onClick={() => setUserMenuOpen(false)}
                              className="text-[10px] font-bold text-sky-600 hover:text-sky-700" data-testid="upgrade-link-menu">
                              UPGRADE
                            </Link>
                          </div>
                        )}
                      </div>
                      <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setUserMenuOpen(false)} data-testid="dashboard-link">
                        <LayoutDashboard size={14} /> Dashboard
                      </Link>
                      {user.slug && (
                        <Link to={`/dr/${user.slug}`} className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setUserMenuOpen(false)} data-testid="view-website-link">
                          <ExternalLink size={14} /> View My Website
                        </Link>
                      )}
                      {user.tier !== 'premium' && user.tier !== 'turbo' && (
                        <Link to="/upgrade" className="flex items-center gap-2 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50" onClick={() => setUserMenuOpen(false)} data-testid="upgrade-link">
                          <CreditCard size={14} /> Upgrade to Premium
                        </Link>
                      )}
                    </>
                  )}
                  {!isDoctor && !isAdmin && (
                    <>
                      <Link to="/my-clinics" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setUserMenuOpen(false)} data-testid="my-clinics-link">
                        <Building2 size={14} /> My Trusted Medical Clinics
                      </Link>
                      <Link to="/bookings" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setUserMenuOpen(false)} data-testid="my-bookings-link">
                        <Calendar size={14} /> My Appointments
                      </Link>
                    </>
                  )}
                  <button onClick={() => { logout(); setUserMenuOpen(false); navigate('/login', { replace: true }); }} className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left" data-testid="logout-button">
                    <LogOut size={14} /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-col items-end gap-1.5">
                <Link to="/login" className="font-bold text-sky-600 hover:text-sky-700 transition-colors px-5 py-2.5 rounded-full border border-sky-200 hover:bg-sky-50 text-center min-w-[9rem]" style={{ fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }} data-testid="login-link">
                  Doctor Login
                </Link>
                <Link to="/login?as=patient" className="font-semibold text-slate-600 hover:text-sky-600 transition-colors px-3 py-1 rounded-full border border-slate-200 hover:bg-slate-50 text-center min-w-[7rem] text-[11px]" data-testid="patient-login-link">
                  Patient Login
                </Link>
              </div>
              <Link to="/register?as=doctor&from=header" className="rounded-full px-7 py-3 bg-sky-600 text-white font-medium hover:bg-sky-700 transition-colors" style={{ fontSize: 'clamp(0.875rem, 0.95vw, 1.0625rem)' }} data-testid="register-link">
                Get Started in 3 Clicks, FREE
              </Link>
            </>
          )}
        </div>

        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 text-slate-600" data-testid="mobile-menu-toggle">
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-6 py-4 space-y-3">
          {isAdmin ? (
            <>
              <Link to="/admin" className="block text-sm font-medium text-slate-600 py-2" onClick={() => setMobileOpen(false)}>Admin Console</Link>
              <button onClick={() => { logout(); setMobileOpen(false); navigate('/login', { replace: true }); }} className="block text-sm text-red-600 py-2">Logout</button>
            </>
          ) : isDoctor ? (
            <>
              <Link to="/dashboard" className="block text-sm font-medium text-slate-600 py-2" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              {user.slug && <Link to={`/dr/${user.slug}`} className="block text-sm text-slate-600 py-2" onClick={() => setMobileOpen(false)}>My Website</Link>}
              {user.tier !== 'premium' && (
                <Link to="/upgrade" className="block text-sm text-amber-700 font-medium py-2" onClick={() => setMobileOpen(false)}>Upgrade to Premium</Link>
              )}
              <button onClick={() => { logout(); setMobileOpen(false); navigate('/login', { replace: true }); }} className="block text-sm text-red-600 py-2">Logout</button>
            </>
          ) : user ? (
            <>
              <Link to="/my-clinics" className="block text-sm text-slate-600 py-2" onClick={() => setMobileOpen(false)}>My Trusted Medical Clinics</Link>
              <Link to="/bookings" className="block text-sm text-slate-600 py-2" onClick={() => setMobileOpen(false)}>My Appointments</Link>
              <button onClick={() => { logout(); setMobileOpen(false); navigate('/login', { replace: true }); }} className="block text-sm text-red-600 py-2">Logout</button>
            </>
          ) : (
            <div className="flex flex-col gap-3 pt-2">
              <Link to="/for-patients" className="block text-sm text-slate-600 font-medium py-1" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-for-patients">For Patients</Link>
              <Link to="/insights" className="block text-sm text-slate-600 font-medium py-1" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-insights">Medical Insights</Link>
              <Link to="/for" className="block text-sm text-slate-600 font-medium py-1" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-specialties">Specialties</Link>
              <Link to="/whats-new" className="flex items-center gap-1.5 text-sm text-sky-700 font-semibold py-1" onClick={() => { markLatestSeen(); setUnseenLaunch(false); setMobileOpen(false); }} data-testid="mobile-nav-whats-new">
                <Sparkles size={13} className="text-sky-500" />
                What's New
                {unseenLaunch && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />}
              </Link>
              <div className="flex gap-3">
                <Link to="/login" className="text-sm text-sky-600 font-bold py-2 px-4 border border-sky-200 rounded-full" onClick={() => setMobileOpen(false)}>Doctor Login</Link>
                <Link to="/register?as=doctor&from=header_mobile" className="rounded-full px-5 py-2 bg-sky-600 text-white text-sm font-medium" onClick={() => setMobileOpen(false)}>Get Started</Link>
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
